import { useMemo } from 'react'
import { PathNames, USER_ROLES, ROLES } from 'config'
import history from 'utils/history'
import NAVIGATION from 'routes/navigation'
import { useParams } from 'react-router-dom'
import { copy, getRoleFromPath } from 'utils/helper'
import { RootState } from 'App'
import { isUndefined } from 'lodash'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

const useNavigationItems = () => {
  const params = useParams()
  const tenantId = useMemo(() => params?.tenantId, [params])
  const path = history.location.pathname
  const userId = useSelector((state: RootState) => state.user.user.id)
  const userRoles = useSelector((state: RootState) => state.user.user.roles || [])
  const isDataTabFeatureFlagEnabled = useFeatureFlag(userId, 'release-data-tab')
  const isMeetingRecordsEnabled = useFeatureFlag(userId, 'release-ai-powered-meetings-user')

  const items: any[] = useMemo(() => {
    let role = getRoleFromPath()
    if (!role || !(role in NAVIGATION)) return []

    // Check if user actually has BSO_ADMIN role (not just based on URL path)
    const hasAdminRights = userRoles.includes(USER_ROLES.BSO_ADMIN)

    // If user has admin rights, use admin navigation (ROLES.BSO_ADMIN) instead of their current path role
    if (hasAdminRights && role !== ROLES.BSO_ADMIN) {
      role = ROLES.BSO_ADMIN
    }

    return NAVIGATION[role]
      .filter((item) => {
        // Filter out Insights / Data / Data & Reports tabs together if flag is off or loading
        if (
          (item.id === 'insights' ||
            item.id === 'data' ||
            item.id === 'dataAndReports' ||
            item.id === 'appData') &&
          (!isDataTabFeatureFlagEnabled || isUndefined(isDataTabFeatureFlagEnabled))
        ) {
          return false
        }

        // Filter out Meeting Records tab if feature flag is off or still loading
        if (
          item.id === 'meetingRecords' &&
          (!isMeetingRecordsEnabled || isUndefined(isMeetingRecordsEnabled))
        ) {
          return false
        }

        // Filter out User Management tab if user doesn't have admin rights
        // This ensures that when admin rights are revoked, the tab disappears
        if (item.label === 'User Management' || item.id === 'userManagement') {
          return hasAdminRights
        }

        return true
      })
      .map((it) => {
        const item = copy(it)
        if (item.path && tenantId) {
          item.path = item.path.replace(':tenantId', tenantId)
        }

        return {
          ...item,
          selected: path.includes(PathNames[item.label]),
        }
      })
  }, [path, tenantId, isDataTabFeatureFlagEnabled, isMeetingRecordsEnabled, userRoles])

  return items
}

export default useNavigationItems
